/**
 * Error Boundary with Tracking Integration
 * 
 * This component catches unhandled errors in the React component tree
 * and tracks them using the tracking SDK.
 * 
 * Requirements: 2.7 (ERROR event tracking)
 */

import React, { Component } from 'react';
import { TrackingContext } from '@rehrig/tracking-sdk';

class TrackedErrorBoundary extends Component {
  static contextType = TrackingContext;

  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[TrackedErrorBoundary] Caught error:', error, errorInfo);

    // Track the error using the tracking context
    const trackingContext = this.context;
    
    if (trackingContext?.queue && trackingContext?.isEnabled) {
      const errorEvent = {
        event_type: 'ERROR',
        timestamp: new Date().toISOString(),
        session_id: this.getSessionId(),
        app_name: trackingContext.appName || 'iris-portal',
        error_name: error.name || 'Error',
        error_message: error.message || 'Unknown error',
        stack_trace: (error.stack || '').substring(0, 5000), // Limit to 5000 chars
        source_url: typeof window !== 'undefined' ? window.location.href : '',
        component_stack: errorInfo.componentStack || '',
        user_context: {
          environment: process.env.NODE_ENV || 'development',
        },
      };

      // Enqueue the error event
      trackingContext.queue.enqueue(errorEvent).catch((err) => {
        console.error('[TrackedErrorBoundary] Failed to track error:', err);
      });
    }
  }

  getSessionId() {
    // Try to get session ID from sessionStorage
    if (typeof window !== 'undefined' && window.sessionStorage) {
      let sessionId = sessionStorage.getItem('tracking_session_id');
      if (!sessionId) {
        sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
        sessionStorage.setItem('tracking_session_id', sessionId);
      }
      return sessionId;
    }
    return `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>Something went wrong</h2>
          <p>We apologize for the inconvenience. The error has been logged.</p>
          <button
            onClick={() => {
              this.setState({ hasError: false, error: null });
              window.location.reload();
            }}
            style={{
              padding: '10px 20px',
              backgroundColor: '#2027A5',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default TrackedErrorBoundary;
